import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Plane, X, Loader2 } from 'lucide-react';
import { getFlightStatus, FlightStatus as ApiFlightStatus, getFlightStatusLabel } from "@/utils/flightStatus";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

interface FlightStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flightNumber: string;
  departureCode: string;
  arrivalCode: string;
  scheduledDeparture: string;
  scheduledArrival?: string;
  loading?: boolean;
  statusError?: string | null;
  flightStatus?: ApiFlightStatus;
}

// Match the structure from the API
interface FlightStatusDetails {
  departureTerminal?: string;
  departureGate?: string;
  departureCheckInDesk?: string;
  estimatedDepartureLocalDate?: string;
  actualDepartureLocalDate?: string;
  estimatedArrivalLocalDate?: string;
  actualArrivalLocalDate?: string;
}

const FlightStatusDialog: React.FC<FlightStatusDialogProps> = ({
  open,
  onOpenChange,
  flightNumber,
  departureCode,
  arrivalCode,
  scheduledDeparture,
  scheduledArrival,
  loading: externalLoading,
  statusError: externalError,
  flightStatus: externalFlightStatus,
}) => {
  // Initialize loading to true by default to prevent the N/A fields from showing briefly
  const [loading, setLoading] = useState(true);
  const [flightStatus, setFlightStatus] = useState<ApiFlightStatus | null>(externalFlightStatus || null);
  const [statusError, setStatusError] = useState<string | null>(externalError || null);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Reset states when dialog opens/closes
  useEffect(() => {
    if (open) {
      // When opening, set loading to true immediately
      setLoading(externalLoading !== undefined ? externalLoading : true);
      setFlightStatus(externalFlightStatus || null);
      setStatusError(externalError || null);
    }
  }, [open, externalLoading, externalError, externalFlightStatus]);

  // Only fetch flight status once when the dialog opens
  useEffect(() => {
    if (open && !externalFlightStatus) {
      // Set loading to true immediately when dialog opens
      setLoading(true);
      fetchFlightStatus();
    }
  }, [open, flightNumber, externalFlightStatus]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (externalLoading !== undefined) {
      setLoading(externalLoading);
    }
  }, [externalLoading]);

  useEffect(() => {
    if (externalError !== undefined) {
      setStatusError(externalError);
    }
  }, [externalError]);

  useEffect(() => {
    if (externalFlightStatus) {
      setFlightStatus(externalFlightStatus);
    }
  }, [externalFlightStatus]);

  const fetchFlightStatus = async () => {
    try {
      setStatusError(null);
      
      // Format the flight ID as EK565_BLR_2025-03-21
      console.debug('Fetching status for flight:', { flightNumber, departureCode, scheduledDeparture });
      
      // Check if scheduledDeparture is valid before using it
      if (!scheduledDeparture) {
        setStatusError("Missing departure date information");
        setLoading(false);
        return;
      }
      
      // Use a safer approach to format the date
      let formattedDate;
      try {
        // Try to parse the date safely
        const date = new Date(scheduledDeparture);
        if (isNaN(date.getTime())) {
          throw new Error("Invalid date");
        }
        formattedDate = date.toISOString().split('T')[0];  // YYYY-MM-DD
      } catch (error) {
        console.error("Error formatting date:", error);
        setStatusError("Invalid departure date format");
        setLoading(false);
        return;
      }
      
      const formattedFlightId = `${flightNumber}_${departureCode}_${formattedDate}`;
      console.debug('Fetching status with ID:', formattedFlightId);
      
      const statusData = await getFlightStatus([formattedFlightId]);
      console.debug('Received status data:', statusData);
      if (statusData.length === 0) {
        setStatusError("No flight status information available");
      } else {
        setFlightStatus(statusData[0]);
      }
    } catch (error) {
      console.error("Error fetching flight status:", error);
      setStatusError("Failed to fetch flight status");
    } finally {
      setLoading(false);
    }
  };

  const formatDateStr = (dateString: string, formatStr: string) => {
    try {
      const dateObj = parseISO(dateString || "1970");
      return format(dateObj, formatStr);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  if (!open) return null;

  // Helper to safely get properties that might not exist in the API response
  const getLatestStatusProperty = (propertyName: string): string => {
    if (!flightStatus || !flightStatus.latestStatus) return 'N/A';
    return (flightStatus.latestStatus as any)[propertyName] || 'N/A';
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={() => onOpenChange(false)}
      style={{ backdropFilter: 'blur(2px)' }}
    >
      <div 
        className="bg-white rounded-lg shadow-lg max-w-[500px] w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-travel-600" />
            <h2 className="font-semibold text-lg">{t("tripCard.flightStatus")} - {flightNumber}</h2>
          </div>
          <button 
            onClick={() => onOpenChange(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="text-center text-gray-600 text-sm py-2 border-b">
          {departureCode} → {arrivalCode}
        </div>
        
        <div className="p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center p-8">
              <Loader2 className="h-8 w-8 text-travel-600 animate-spin mb-4" />
              <p className="text-gray-600">{t("common.loading", "Loading...")}</p>
            </div>
          ) : statusError || !flightStatus ? (
            <div className="p-6 text-center">
              <p className="text-gray-600">{t("tripCard.noFlightStatusAvailable", "No flight status information available")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-b border-gray-200 pb-2">
                    <strong className="text-sm font-medium">Estimated Departure:</strong>
                    <div className="text-base font-semibold">
                      {flightStatus?.latestStatus?.estimatedDepartureLocalDate 
                        ? formatDateStr(flightStatus.latestStatus.estimatedDepartureLocalDate, 'HH:mm') 
                        : 'N/A'}
                      {flightStatus?.scheduledDepartureLocalDate && flightStatus?.latestStatus?.estimatedDepartureLocalDate && (() => {
                        const departureStatus = getFlightStatusLabel(
                          flightStatus.scheduledDepartureLocalDate,
                          flightStatus.latestStatus.estimatedDepartureLocalDate
                        );
                        return (
                          <span className={`ml-2 text-sm ${departureStatus.color} hidden sm:inline-block`}>
                            ({departureStatus.label})
                          </span>
                        );
                      })()}
                    </div>
                    {/* Mobile view status - show on new line */}
                    {flightStatus?.scheduledDepartureLocalDate && flightStatus?.latestStatus?.estimatedDepartureLocalDate && (() => {
                      const departureStatus = getFlightStatusLabel(
                        flightStatus.scheduledDepartureLocalDate,
                        flightStatus.latestStatus.estimatedDepartureLocalDate
                      );
                      return (
                        <div className={`text-sm ${departureStatus.color} block sm:hidden mt-1`}>
                          {departureStatus.label}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="border-b border-gray-200 pb-2">
                    <strong className="text-sm font-medium">Estimated Arrival:</strong>
                    <div className="text-base font-semibold">
                      {flightStatus?.latestStatus?.estimatedArrivalLocalDate 
                        ? formatDateStr(flightStatus.latestStatus.estimatedArrivalLocalDate, 'HH:mm') 
                        : 'N/A'}
                      {flightStatus?.scheduledArrivalLocalDate && flightStatus?.latestStatus?.estimatedArrivalLocalDate && (() => {
                        const arrivalStatus = getFlightStatusLabel(
                          flightStatus.scheduledArrivalLocalDate,
                          flightStatus.latestStatus.estimatedArrivalLocalDate
                        );
                        return (
                          <span className={`ml-2 text-sm ${arrivalStatus.color} hidden sm:inline-block`}>
                            ({arrivalStatus.label})
                          </span>
                        );
                      })()}
                    </div>
                    {/* Mobile view status - show on new line */}
                    {flightStatus?.scheduledArrivalLocalDate && flightStatus?.latestStatus?.estimatedArrivalLocalDate && (() => {
                      const arrivalStatus = getFlightStatusLabel(
                        flightStatus.scheduledArrivalLocalDate,
                        flightStatus.latestStatus.estimatedArrivalLocalDate
                      );
                      return (
                        <div className={`text-sm ${arrivalStatus.color} block sm:hidden mt-1`}>
                          {arrivalStatus.label}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="border-b border-gray-200 pb-2">
                    <strong className="text-sm font-medium">Terminal:</strong>
                    <div className="text-base font-semibold">{getLatestStatusProperty('departureTerminal')}</div>
                  </div>
                  <div className="border-b border-gray-200 pb-2">
                    <strong className="text-sm font-medium">Gate:</strong>
                    <div className="text-base font-semibold">{getLatestStatusProperty('departureGate')}</div>
                  </div>
                  <div className="border-b border-gray-200 pb-2">
                    <strong className="text-sm font-medium">Check-In Desk:</strong>
                    <div className="text-base font-semibold">{getLatestStatusProperty('departureCheckInDesk')}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlightStatusDialog;
